import React, { useState, useEffect } from 'react';

interface LogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showSubtitle?: boolean;
  customLogoUrl?: string | null;
}

export const Logo: React.FC<LogoProps> = ({ 
  className = '', 
  size = 'md',
  showSubtitle = true,
  customLogoUrl
}) => {
  const getInitialLogo = () => {
    if (customLogoUrl !== undefined && customLogoUrl !== null) return customLogoUrl;
    const stored = localStorage.getItem('customLogoUrl');
    if (stored) return stored;
    return '/imagens/logo.png';
  };

  const [logoSrc, setLogoSrc] = useState<string | null>(getInitialLogo);

  useEffect(() => {
    if (customLogoUrl !== undefined) {
      setLogoSrc(customLogoUrl || '/imagens/logo.png');
    } else {
      const stored = localStorage.getItem('customLogoUrl');
      setLogoSrc(stored || '/imagens/logo.png');
    }

    const handleStorage = () => {
      const updated = localStorage.getItem('customLogoUrl');
      setLogoSrc(updated || '/imagens/logo.png');
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('logoUpdated', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('logoUpdated', handleStorage);
    };
  }, [customLogoUrl]);

  // Height map for responsive rendering - enhanced for high visibility on desktop & laptops
  const heightMap = {
    sm: 'h-9 sm:h-10 md:h-11',
    md: 'h-12 sm:h-14 md:h-16 lg:h-18 xl:h-20',
    lg: 'h-16 sm:h-20 md:h-24 lg:h-28',
    xl: 'h-20 sm:h-26 md:h-32 lg:h-36'
  };

  if (logoSrc) {
    return (
      <div className={`inline-flex items-center select-none ${className}`}>
        <img
          src={logoSrc}
          alt="Logo Do Japão - redirecionamento do Japão para o Brasil"
          className={`w-auto ${heightMap[size]} max-w-[280px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[480px] object-contain transition-all hover:scale-102`}
          onError={() => setLogoSrc(null)}
          referrerPolicy="no-referrer"
        />
      </div>
    );
  }

  return (
    <div className={`inline-flex items-center select-none ${className}`}>
      <svg
        viewBox="0 0 960 280"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`w-auto ${heightMap[size]} max-w-[280px] sm:max-w-[340px] md:max-w-[400px] lg:max-w-[480px] transition-all hover:scale-102`}
        aria-label="Do Japão até a sua casa"
      >
        <g id="LogoSymbol">
          {/* Green Hand (Left) */}
          <path
            d="M 52 12 C 68 -2 94 4 100 24 L 122 118 C 124 126 133 131 141 128 C 149 125 153 116 151 107 L 132 18 C 137 2 158 -4 172 6 C 183 14 183 28 177 42 L 152 106 C 150 114 157 122 165 122 C 173 122 179 115 181 107 L 202 44 C 209 25 231 18 246 30 C 257 40 256 56 246 70 C 210 118 180 172 136 216 C 118 234 92 248 66 252 C 36 256 12 236 4 206 C -4 172 12 138 36 114 C 50 100 72 92 92 92 C 100 92 104 82 98 76 C 82 60 66 42 52 26 C 44 18 44 6 52 12 Z"
            fill="#00A651"
          />

          {/* Yellow Hand (Right) */}
          <path
            d="M 276 36 C 286 18 310 16 324 28 C 336 38 334 56 322 68 L 280 114 C 274 120 276 130 284 132 C 310 138 334 152 352 172 C 372 194 380 224 368 252 C 356 276 330 288 304 286 C 280 284 258 270 244 250 C 230 230 224 204 226 180 C 226 172 216 168 210 174 C 198 188 184 206 166 222 C 154 232 136 228 134 212 C 132 190 148 164 164 146 C 184 124 210 106 238 94 L 270 82 C 276 80 278 72 274 68 L 260 54 C 254 46 264 36 276 36 Z"
            fill="#FFD100"
          />

          {/* White Border outline gap around Red Heart */}
          <path
            d="M 180 114 C 158 86 118 84 92 112 C 68 136 68 174 94 200 L 180 272 L 266 200 C 292 174 292 136 268 112 C 242 84 202 86 180 114 Z"
            fill="#FFFFFF"
          />

          {/* Inner Red Heart */}
          <path
            d="M 180 124 C 162 98 126 96 102 122 C 82 142 82 174 104 196 L 180 262 L 256 196 C 278 174 278 142 258 122 C 234 96 198 98 180 124 Z"
            fill="#ED1C24"
          />
        </g>

        {/* Text Group "Do Japão" */}
        <g id="LogoTextDoJapao">
          <text
            x="400"
            y="165"
            fill="#0080FF"
            fontFamily="'Arial Black', 'Impact', 'Trebuchet MS', 'Helvetica Neue', sans-serif"
            fontWeight="900"
            fontSize="185"
            letterSpacing="-3"
          >
            Do Japão
          </text>
        </g>

        {/* Text Group "até a sua casa" */}
        {showSubtitle && (
          <g id="LogoTextAteASuaCasa">
            <text
              x="404"
              y="252"
              fill="#FA8223"
              fontFamily="'Brush Script MT', 'Caveat', 'Dancing Script', 'Pacifico', 'Comic Sans MS', cursive, sans-serif"
              fontWeight="bold"
              fontStyle="italic"
              fontSize="96"
              letterSpacing="1"
            >
              até a sua casa
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default Logo;
